import React, { useState, useEffect } from 'react';
import { Box, Grid, IconButton, Typography, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowLeft, Check, MessageSquare, DollarSign, Award } from 'lucide-react';
import ProposalHeader from '../proposal/ProposalHeader.tsx';
import ProposedSolution from '../proposal/ProposedSolution.tsx';
import CompanyDescription from '../proposal/CompanyDescription.tsx';
import ObjectivesKeyResults from '../proposal/ObjectivesKeyResults.tsx';
import ProcessComparison from '../proposal/ProcessComparison.tsx';
import ProfitRiskAnalysis from '../proposal/ProfitRiskAnalysis.tsx';
import UseCaseComparison from '../proposal/UseCaseComparison.tsx';
import { proposals, companies, solutions, processes, profitRisks, useCases } from '../../data/mockData.ts';
import FeedbackPanel from './FeedbackPanel.tsx';
import PaymentPanel from './PaymentPanel.tsx';
import AttributionPanel from './AttributionPanel.tsx';

interface ProposalDetailContentProps {
  proposalId: string | null;
  onBack: () => void;
  onViewSolution: (id: string) => void;
}

const ContentContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  color: '#8a2be2',
  '&:hover': {
    background: 'rgba(138, 43, 226, 0.1)',
  },
}));

const SectionContainer = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
  position: 'relative',
}));

const FeedbackButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(1),
  background: 'linear-gradient(45deg, #8A2BE2 30%, #4B0082 90%)',
  color: '#fff',
  '&:hover': {
    background: 'linear-gradient(45deg, #9932CC 30%, #8A2BE2 90%)',
  },
}));

const AcceptButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #00CC99 30%, #008B8B 90%)',
  color: '#fff',
  '&:hover': {
    background: 'linear-gradient(45deg, #00E676 30%, #00CC99 90%)',
  },
}));

const SolutionCard = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.8)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 30px rgba(138, 43, 226, 0.3)',
    border: '1px solid rgba(138, 43, 226, 0.5)',
  },
}));

const ProposalDetailContent: React.FC<ProposalDetailContentProps> = ({
  proposalId,
  onBack,
  onViewSolution,
}) => {
  const [proposal, setProposal] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [relatedSolutions, setRelatedSolutions] = useState<any[]>([]);
  
  // State for feedback dialog
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState<boolean>(false);
  const [activeSection, setActiveSection] = useState<'feedback' | 'payment' | 'attribution' | null>(null);

  // eslint-disable-next-line no-unused-vars
  const [editedFields, setEditedFields] = useState<{
    title: string;
    description: string;
    timeline: string;
  }>({
    title: '',
    description: '',
    timeline: '',
  });

  useEffect(() => {
    if (!proposalId) {
      setLoading(false);
      setError(true);
      return;
    }

    // Try to find the proposal in our mock data
    const foundProposal = proposals.find(p => p.id === proposalId);
    
    if (!foundProposal) {
      setLoading(false);
      setError(true);
      return;
    }
    
    // Find related data for this proposal
    const company = companies.find(c => c.id === foundProposal.companyId);
    const solution = solutions.find(s => s.proposalId === foundProposal.id);
    
    // Get process data
    const currentProcess = processes.find(p => p.type === 'current');
    const futureProcess = processes.find(p => p.type === 'future');
    
    // Get profit/risk data
    const currentFinancials = profitRisks.filter(pr => pr.timeline === 'before');
    const futureFinancials = profitRisks.filter(pr => pr.timeline === 'after');
    
    // Get use case data
    const currentUseCases = useCases.filter(uc => uc.timeline === 'current');
    const futureUseCases = useCases.filter(uc => uc.timeline === 'future');
    
    // Create a complete proposal object with all the required data
    const completeProposal = {
      id: foundProposal.id,
      title: foundProposal.title,
      description: foundProposal.description,
      date: foundProposal.createdAt ? new Date(foundProposal.createdAt).toLocaleDateString() : new Date().toLocaleDateString(),
      timeline: foundProposal.estimatedTimeline || '3 months',
      timelineType: foundProposal.timelineType || 'initiative',
      status: foundProposal.status || 'draft',
      
      company: {
        name: company?.name || 'Unknown Company',
        description: company?.description || 'No description available',
        vision: company?.vision || 'No vision statement',
        mission: company?.mission || 'No mission statement'
      },
      
      solution: {
        id: solution?.id || '1',
        title: solution?.title || 'Default Solution',
        description: solution?.description || 'No solution description available',
        cost: `$${solution?.estimatedCost?.toLocaleString() || '0'}`,
        timeframe: solution?.estimatedTimeframe || '3 months'
      },
      
      objectives: company?.objectives || [],
      
      currentProcess: {
        title: currentProcess?.name || 'Current Process',
        description: currentProcess?.description || 'No process description',
        steps: currentProcess?.steps.map((step, index) => ({ id: `c-${index}`, description: step })) || []
      },
      
      futureProcess: {
        title: futureProcess?.name || 'Future Process',
        description: futureProcess?.description || 'No process description',
        steps: futureProcess?.steps.map((step, index) => ({ id: `f-${index}`, description: step })) || []
      },
      
      currentFinancials: currentFinancials.map(item => ({
        id: item.id,
        description: item.description,
        amount: item.financialImpact,
        type: item.type
      })),
      
      futureFinancials: futureFinancials.map(item => ({
        id: item.id,
        description: item.description,
        amount: item.financialImpact,
        type: item.type
      })),
      
      currentUseCases: currentUseCases.map(item => ({
        id: item.id,
        title: item.title,
        description: item.description
      })),
      
      futureUseCases: futureUseCases.map(item => ({
        id: item.id,
        title: item.title,
        description: item.description
      }))
    };
    
    setProposal(completeProposal);

    // Find related solutions
    const foundSolutions = solutions.filter((s) => s.proposalId === proposalId);
    setRelatedSolutions(foundSolutions);

    setLoading(false);
  }, [proposalId]);

  const handleFeedbackDialogOpen = () => {
    setFeedbackDialogOpen(true);
    setActiveSection('feedback');
  };

  const handleDialogClose = () => {
    setFeedbackDialogOpen(false);
    setActiveSection(null);
  };

  const handleSwitchSection = (section: 'feedback' | 'payment' | 'attribution') => {
    setActiveSection(section);
  };

  const handleAcceptSolution = (solutionId: string) => {
    // Update solution status to accepted
    const updatedSolutions = relatedSolutions.map(sol => 
      sol.id === solutionId ? { ...sol, status: 'accepted' } : sol
    );
    setRelatedSolutions(updatedSolutions);
    
    // Open payment dialog
    setFeedbackDialogOpen(true);
    setActiveSection('payment');
  };

  // eslint-disable-next-line no-unused-vars
  const handleInputChange = (field: keyof typeof editedFields, value: string) => {
    setEditedFields({
      ...editedFields,
      [field]: value
    });
  };

  // eslint-disable-next-line no-unused-vars
  const handleSaveField = (field: keyof typeof editedFields) => {
    if (proposal) {
      setProposal({
        ...proposal,
        [field]: editedFields[field]
      });
      
      // You might want to add an API call here to persist changes
      console.log(`Saved ${field} with value: ${editedFields[field]}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1">Loading proposal details...</Typography>
      </Box>
    );
  }

  if (error || !proposal) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <BackButton onClick={onBack}>
          <ArrowLeft />
        </BackButton>
        <Typography variant="body1" sx={{ mt: 4 }}>
          Proposal data not found. The proposal may have been deleted or is unavailable.
        </Typography>
        <Button 
          variant="contained" 
          onClick={onBack}
          sx={{ mt: 2 }}
        >
          Return to Solutions
        </Button>
      </Box>
    );
  }

  return (
    <ContentContainer>
      <BackButton onClick={onBack}>
        <ArrowLeft />
      </BackButton>

      <ProposalHeader
        title={proposal.title}
        date={proposal.date}
        timeline={proposal.timeline}
        timelineType={proposal.timelineType}
        status={proposal.status}
        description={proposal.description}
        isEditable={true}
        onInputChange={handleInputChange}
        onSave={handleSaveField}
        editedFields={editedFields}
      />

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <ProposedSolution
            title={proposal.solution.title}
            description={proposal.solution.description}
            cost={proposal.solution.cost}
            timeframe={proposal.solution.timeframe}
            solutionId={proposal.solution.id}
            onViewDetails={() => onViewSolution(proposal.solution.id)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CompanyDescription
            companyName={proposal.company.name}
            description={proposal.company.description}
            vision={proposal.company.vision}
            mission={proposal.company.mission}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ObjectivesKeyResults objectives={proposal.objectives} />
        </Grid>

        <Grid item xs={12}>
          <ProcessComparison
            currentTitle={proposal.currentProcess.title}
            currentDescription={proposal.currentProcess.description}
            currentSteps={proposal.currentProcess.steps}
            futureTitle={proposal.futureProcess.title}
            futureDescription={proposal.futureProcess.description}
            futureSteps={proposal.futureProcess.steps}
          />
        </Grid>

        <Grid item xs={12}>
          <ProfitRiskAnalysis
            currentItems={proposal.currentFinancials}
            futureItems={proposal.futureFinancials}
          />
        </Grid>

        <Grid item xs={12}>
          <UseCaseComparison
            currentUseCases={proposal.currentUseCases}
            futureUseCases={proposal.futureUseCases}
          />
        </Grid>
      </Grid>

      {/* Proposed Solutions Section */}
      <SectionContainer>
        <Typography variant="h6" sx={{ color: '#fff', mb: 3 }}>
          Proposed Solutions
        </Typography>

        {relatedSolutions.length === 0 ? (
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', my: 4 }}>
            No solutions have been proposed yet.
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {relatedSolutions.map((solution) => (
              <Grid item xs={12} key={solution.id}>
                <SolutionCard onClick={() => onViewSolution(solution.id)}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ color: '#fff' }}>
                      {solution.title}
                    </Typography>
                    <Box>
                      {solution.status === 'accepted' && (
                        <Chip 
                          icon={<Check size={16} />} 
                          label="Accepted" 
                          sx={{ 
                            background: 'rgba(0, 204, 153, 0.3)',
                            color: '#00CC99',
                            mr: 1
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                    {solution.description.substring(0, 150)}
                    {solution.description.length > 150 ? '...' : ''}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#00CC99' }}>
                      Est. Cost: ${solution.estimatedCost?.toLocaleString() || '0'}
                    </Typography>
                    
                    <Box>
                      <FeedbackButton
                        startIcon={<MessageSquare size={16} />}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFeedbackDialogOpen();
                        }}
                      >
                        Feedback
                      </FeedbackButton>
                      
                      {solution.status !== 'accepted' && (
                        <AcceptButton
                          startIcon={<Check size={16} />}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAcceptSolution(solution.id);
                          }}
                        >
                          Accept
                        </AcceptButton>
                      )}
                    </Box>
                  </Box>
                </SolutionCard>
              </Grid>
            ))}
          </Grid>
        )}
      </SectionContainer>

      {/* Feedback/Payment/Attribution Dialog */}
      <Dialog 
        open={feedbackDialogOpen} 
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(26, 26, 46, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            border: '1px solid rgba(138, 43, 226, 0.3)',
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(138, 43, 226, 0.2)' }}>
          <Typography variant="h6" sx={{ color: '#fff' }}>
            {activeSection === 'feedback' && 'Provide Feedback'}
            {activeSection === 'payment' && 'Process Payment'}
            {activeSection === 'attribution' && 'Attribution & Recognition'}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', mb: 3 }}>
            <Button 
              variant={activeSection === 'feedback' ? 'contained' : 'outlined'}
              startIcon={<MessageSquare size={16} />}
              onClick={() => handleSwitchSection('feedback')}
              sx={{ 
                mr: 1,
                ...(activeSection === 'feedback' ? {
                  background: 'linear-gradient(45deg, #8A2BE2 30%, #4B0082 90%)',
                } : {
                  borderColor: 'rgba(138, 43, 226, 0.5)',
                  color: '#fff',
                })
              }}
            >
              Feedback
            </Button>
            
            <Button 
              variant={activeSection === 'payment' ? 'contained' : 'outlined'}
              startIcon={<DollarSign size={16} />}
              onClick={() => handleSwitchSection('payment')}
              sx={{ 
                mr: 1,
                ...(activeSection === 'payment' ? {
                  background: 'linear-gradient(45deg, #00CC99 30%, #008B8B 90%)',
                } : {
                  borderColor: 'rgba(0, 204, 153, 0.5)',
                  color: '#fff',
                })
              }}
            >
              Payment
            </Button>
            
            <Button 
              variant={activeSection === 'attribution' ? 'contained' : 'outlined'}
              startIcon={<Award size={16} />}
              onClick={() => handleSwitchSection('attribution')}
              sx={{ 
                ...(activeSection === 'attribution' ? {
                  background: 'linear-gradient(45deg, #FFD700 30%, #FFA500 90%)',
                } : {
                  borderColor: 'rgba(255, 215, 0, 0.5)',
                  color: '#fff',
                })
              }}
            >
              Attribution
            </Button>
          </Box>
          
          {activeSection === 'feedback' && <FeedbackPanel proposalId={proposalId} />}
          {activeSection === 'payment' && <PaymentPanel proposalId={proposalId} solutions={relatedSolutions} />}
          {activeSection === 'attribution' && <AttributionPanel proposalId={proposalId} />}
        </DialogContent>
        
        <DialogActions sx={{ borderTop: '1px solid rgba(138, 43, 226, 0.2)', p: 2 }}>
          <Button 
            onClick={handleDialogClose}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </ContentContainer>
  );
};

export default ProposalDetailContent; 